import { API_URL } from '@/modules/shared/constants/API_URL'
import { httpAuthenticationInterceptor } from '@/modules/shared/modules/http/domain/services/HTTPAuthenticationInterceptor'
import HTTPExceptionInterceptor from '@/modules/shared/modules/http/domain/services/HTTPExceptionInterceptor'
import { httpTokenInterceptor } from '@/modules/shared/modules/http/domain/services/HTTPTokenInterceptor'
import { PersonaInfoSummary } from '@/modules/shared/schemas/PersonaInfoSummary'

import { AreaAgentOrResponsibleList } from '../../domain/models/AreaAgentOrResponsible'
import { AreaPriorities } from '../../domain/models/AreaPriorities'
import { AreaSummaryList } from '../../domain/models/AreaSummary'
import { AreaTicketIndicators } from '../../domain/models/AreaTicketIndicators'
import { AreaTickets } from '../../domain/models/AreaTickets'
import { CompletedTicketsByArea } from '../../domain/models/CompletedTicketsByArea'
import {
  DashboardTicket,
  DashboardTicketDTO
} from '../../domain/models/DashboardTicket'
import { SlaAnalysisMetrics } from '../../domain/models/IndicatorsMetrics'
import {
  Area,
  AreaResponsible,
  ListAreas,
  ListOfAreasByName
} from '../../domain/models/ListAreas'
import {
  EditNotificationAreaDTO,
  NotificationArea
} from '../../domain/models/NotificationArea'
import { OperationalListArea } from '../../domain/models/OperationalListArea'
import { ResponsibleTicketIndicators } from '../../domain/models/ResponsibleTicketIndicators'
import { SubArea } from '../../domain/models/SubArea'
import {
  ListTickets,
  ListTicketsWithCensored,
  Ticket,
  TicketDate
} from '../../domain/models/Ticket'
import { TicketDetails } from '../../domain/models/TicketDetails'
import { TicketDatasourceAdapter } from '../../infra/datasources/TicketDatasource'

export const TicketDatasource: TicketDatasourceAdapter = {
  async listTicket(data: DashboardTicketDTO) {
    const {
      date,
      areaId,
      subAreaId,
      responsible,
      page,
      size,
      status,
      month,
      year
    } = data
    const params = new URLSearchParams()
    if (date) params.append('date', date)
    if (month) params.append('month', month.toString())
    if (year) params.append('year', year.toString())
    if (areaId) params.append('areaId', areaId)
    if (subAreaId) params.append('subAreaId', subAreaId)
    if (responsible) {
      params.append('responsibleId', responsible)
    }
    if (status) status.forEach((status) => params.append('status', status))
    if (page) params.append('page', page?.toString())
    if (size) params.append('size', size?.toString())
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/tickets?${params.toString()}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return DashboardTicket.parse(await response.json())
  },

  async createTicket(data) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/tickets`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    const json = Ticket.parse(await response.json())
    return json
  },

  async ticketDetail(ticketId: string) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/tickets/${ticketId}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return TicketDetails.parse(await response.json())
  },

  async createAssignResponsibility({ ticketId, personaId }) {
    await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/tickets/${ticketId}`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify({ personaId }),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
  },

  async createAssignPriority({ priority, ticketId }) {
    await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/tickets/${ticketId}/tickets`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify({ priority }),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
  },

  async createTicketEvaluation({ ticketId, data }) {
    await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/tickets/${ticketId}/tickets-evaluations`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
  },
  async createTicketStatus({ ticketId, status }) {
    await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/tickets/${ticketId}/status`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify({ status }),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
  },
  async createAssignAttachment({ ticketId, data }) {
    await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/tickets/${ticketId}/attachment`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
  },
  async listTicketForAssignAttachment({ ticketId, areaId }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/tickets/${ticketId}/attachment?areaId=${areaId}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return ListTickets.parse(await response.json())
  },

  async listTicketsPersona({ personaId, date }) {
    const params = new URLSearchParams()
    if (date) params.append('date', date)
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/personas/${personaId}/tickets?${params.toString()}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return ListTickets.parse(await response.json())
  },

  async listTicketsPatient({ patientId, date }) {
    const params = new URLSearchParams()
    if (date) params.append('date', date)
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/patients/${patientId}/tickets?${params.toString()}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return ListTicketsWithCensored.parse(await response.json())
  },

  async listTicketsArea({ areaId, data }) {
    const {
      page,
      size,
      status,
      month,
      year,
      complaintBy,
      priority,
      responsible,
      subAreaId,
      responsibleId,
      search
    } = data
    const params = new URLSearchParams()
    if (page) params.append('page', page.toString())
    if (size) params.append('size', size.toString())
    if (month) params.append('month', month.toString())
    if (search) params.append('search', search.toString())
    if (status) status.forEach((status) => params.append('status', status))
    if (year) params.append('year', year.toString())
    if (complaintBy) params.append('complaintBy', complaintBy)
    if (priority)
      priority.forEach((priority) => params.append('priority', priority))
    if (subAreaId) params.append('subAreaId', subAreaId)
    if (responsibleId) params.append('responsibleId', responsibleId)
    if (responsible)
      responsible.forEach((id) => params.append('responsible', id))

    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/areas/${areaId}/tickets?${params.toString()}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return AreaTickets.parse(await response.json())
  },

  async listCompletedTicketsByArea({ areaId, data }) {
    const { page, size } = data
    const params = new URLSearchParams()
    if (page) params.append('page', page?.toString())
    if (size) params.append('size', size?.toString())
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/areas/${areaId}/tickets/completed?${params.toString()}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return CompletedTicketsByArea.parse(await response.json())
  },
  async listResponsibleTicketIndicators({ data }) {
    const { areaId, month, year, responsibleId } = data
    const params = new URLSearchParams()
    if (responsibleId) params.append('responsibleId', responsibleId)
    if (month) params.append('month', month.toString())
    if (year) params.append('year', year.toString())
    if (areaId) params.append('areaId', areaId)

    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/tickets/responsible/indicators?${params.toString()}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return ResponsibleTicketIndicators.parse(await response.json())
  },

  async listTicketIndicators({ data }) {
    const { areaId, month, year } = data
    const params = new URLSearchParams()
    if (month) params.append('month', month.toString())
    if (year) params.append('year', year.toString())
    if (areaId) params.append('areaId', areaId)
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/tickets/indicators?${params.toString()}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return AreaTicketIndicators.parse(await response.json())
  },
  async listAreas() {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return ListAreas.parse(await response.json())
  },
  async updateSubArea({ areaId, subAreaId, data }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}/sub-area/${subAreaId}`,
          httpTokenInterceptor({
            method: 'PUT',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return SubArea.parse(await response.json())
  },
  async listAreaPriorities({ areaId }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}/priorities`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return AreaPriorities.array().parse(await response.json())
  },
  async updateAreaPriority({ areaId, data }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}/priorities`,
          httpTokenInterceptor({
            method: 'PUT',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return AreaPriorities.array().parse(await response.json())
  },
  async createArea({ data }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return Area.parse(await response.json())
  },
  async createSubArea({ areaId, data }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}/sub-area`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return SubArea.parse(await response.json())
  },
  async listAreaResponsible({ areaId, subareaId }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}/responsible${
            subareaId ? `?subAreaId=${subareaId}` : ''
          }`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return AreaAgentOrResponsibleList.parse(await response.json())
  },
  async createAreaResponsible({ areaId, personaId }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}/responsible`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify(personaId),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return AreaResponsible.parse(await response.json())
  },
  async createAreaAttendant({ areaId, personaId }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}/attendants`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify(personaId),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return AreaResponsible.parse(await response.json())
  },
  async listOperationalAreas({ operationalId }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/operational/${operationalId}/areas`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return OperationalListArea.parse(await response.json())
  },
  async listAreaById({ areaId }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return Area.parse(await response.json())
  },
  async deleteAreaId({ areaId }) {
    await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}`,
          httpTokenInterceptor({
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
  },
  async listTicketDate({ personaId }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/persona/${personaId}/tickets-dates`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return TicketDate.parse(await response.json())
  },
  async listPatientTicketDate({ patientId }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/patients/${patientId}/tickets-dates`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return TicketDate.parse(await response.json())
  },
  async listPersonaTicketArea() {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/personaType`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return AreaSummaryList.parse(await response.json())
  },
  async listNotificationsAreas() {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/notifications/associations`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return NotificationArea.array().parse(await response.json())
  },
  async createNotificationAreaAssociation(data) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/notifications/associations`,
          httpTokenInterceptor({
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return NotificationArea.parse(await response.json())
  },
  async deleteNotificationAreaAssociation(associationId: string) {
    await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/notifications/associations/${associationId}`,
          httpTokenInterceptor({
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
  },
  async editNotificationAreaAssociation({
    associationId,
    data
  }: {
    associationId: string
    data: EditNotificationAreaDTO
  }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/notifications/associations/${associationId}`,
          httpTokenInterceptor({
            method: 'PUT',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return NotificationArea.parse(await response.json())
  },
  async adminUpdateArea({ areaId, data }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}`,
          httpTokenInterceptor({
            method: 'PUT',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return Area.parse(await response.json())
  },
  async operationalUpdateArea({ areaId, data }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/operational/area/${areaId}`,
          httpTokenInterceptor({
            method: 'PUT',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return Area.parse(await response.json())
  },
  async operationalList() {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/persona/operators`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return PersonaInfoSummary.array().parse(await response.json())
  },

  async listNameArea() {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/summaries`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return ListOfAreasByName.parse(await response.json())
  },
  async listFilterTicket({ data }) {
    const { month, year, status, areaId, page, size, responsibleId } = data
    const params = new URLSearchParams()
    if (month) params.append('month', month.toString())
    if (year) params.append('year', year.toString())
    if (status) params.append('status', status)
    if (areaId) params.append('areaId', areaId)
    if (page) params.append('page', page.toString())
    if (size) params.append('size', size.toString())
    if (responsibleId) params.append('responsibleId', responsibleId)

    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/tickets/indicators?${params.toString()}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return CompletedTicketsByArea.parse(await response.json())
  },

  async listAllResponsibleTheArea() {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/subarea/responsible`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return PersonaInfoSummary.array().parse(await response.json())
  },
  async listIndicatorsMetricsTma({ areaId, data }) {
    const { month, year } = data
    const params = new URLSearchParams()
    if (month) params.append('month', month.toString())
    if (year) params.append('year', year.toString())
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}/indicators/tma?${params.toString()}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return SlaAnalysisMetrics.parse(await response.json())
  },
  async listIndicatorsMetricsTme({ areaId, data }) {
    const { month, year } = data
    const params = new URLSearchParams()
    if (month) params.append('month', month.toString())
    if (year) params.append('year', year.toString())
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}/indicators/tme?${params.toString()}`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return SlaAnalysisMetrics.parse(await response.json())
  },

  async listAgentsAndResponsiblesByArea({ areaId }) {
    const response = await HTTPExceptionInterceptor(
      await httpAuthenticationInterceptor(
        await fetch(
          `${API_URL}/v1/area/${areaId}/participants`,
          httpTokenInterceptor({
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        )
      )
    )
    return PersonaInfoSummary.array().parse(await response.json())
  }
}
